/**
 * What an Edit User save sends (SC-439). Cloud judges the fields separately: the role needs
 * `roles.assign`, and department, title and the permissions list need `members.edit`. Sending the
 * whole form would make every save need both, so a person who may only assign roles could never
 * save. Only the fields that differ from the stored member are sent.
 */
import type { CompanyUser } from '../../../core/models/Company'
import type { EditUserFormData } from '../types'
import { memberHasNoRole, memberRoleKey, roleKeyOf } from './roleOptions'

export function userEditChanges(
  user: Pick<
    CompanyUser,
    'role' | 'roleKey' | 'role_is_default' | 'department' | 'title' | 'permissions'
  >,
  form: EditUserFormData,
  /** False without roles.assign: cloud refuses any save that carries a role. */
  options: { canAssignRole?: boolean } = {}
): Partial<EditUserFormData> {
  const { canAssignRole = true } = options
  const changes: Partial<EditUserFormData> = {}
  // Compared as role keys: the form holds `manager` or `user` for a member stored that way, and a
  // custom role's key for a member cloud stores as `user`. Moving off a custom role to Member is
  // a change (`member` against the custom key), a re-pick of the same role is not.
  // A member with no role of their own has nothing stored to compare with: the form shows the
  // default role that applies, and a save writes it (or the role picked instead) explicitly.
  if (canAssignRole && (memberHasNoRole(user) || roleKeyOf(form.role) !== memberRoleKey(user))) {
    changes.role = form.role
  }
  if ((form.department ?? '') !== (user.department || ''))
    changes.department = form.department ?? ''
  if ((form.title ?? '') !== (user.title || '')) changes.title = form.title ?? ''
  const stored = [...(user.permissions || [])].sort()
  const edited = [...(form.permissions ?? [])].sort()
  if (stored.length !== edited.length || stored.some((p, i) => p !== edited[i])) {
    changes.permissions = form.permissions ?? []
  }
  return changes
}
